import { Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { DEFAULT_SCREEN_ALL, ISO_DATE_FORMAT } from 'src/constants';
import { PATHS } from 'src/constants/paths';
import {
  getEarliestStartDateFromDefaultAssemblies,
  getLocationChannelsFromSchedulePreviewContext,
  getMaxPreviewDate,
  getMinPreviewDate,
  getValidDate,
} from 'src/features/locations/schedulePreviewPage/SchedulePreviewPage.helper';
import {
  useGetAllDayparts,
  useGetLocationGroup,
  useParamQuery,
} from 'src/hooks';
import { useGetSchedulePreviewContext } from 'src/hooks/api/schedules/schedulePreview/useGetSchedulePreviewContext';
import { useApiErrorNavigator } from 'src/hooks/navigation';
import {
  IPreviewOption,
  ISchedulePreviewContext,
  ISchedulePreviewWithChangeSetContext,
} from 'src/interfaces/SchedulePreview';

export const useSchedulePreviewPage = () => {
  const { getParamsValue, onChangeParams } = useParamQuery();
  const locationGroupId = getParamsValue('groupID')[0];
  const screen = getParamsValue('screen')[0] ?? DEFAULT_SCREEN_ALL;
  const position = getParamsValue('position')[0] ?? 1;
  const daypartId = getParamsValue('daypartID')[0] ?? 1;
  const date = getParamsValue('date')[0];
  const locationCode = getParamsValue('locationCode')[0];
  const changeSetId = getParamsValue('changeSetID')[0];
  const [previewOptionState, setPreviewOptionState] =
    useState<IPreviewOption | null>(changeSetId ? IPreviewOption.AFTER : null);

  // Synchronously derive the effective preview option.
  // If change_set_id is missing from url, force null immediately. This protects against
  // stale state causing crashes during the render cycle.
  const previewOption = useMemo(() => {
    if (!changeSetId) return null;

    return previewOptionState ?? IPreviewOption.AFTER;
  }, [changeSetId, previewOptionState]);

  const { data: locationGroup, error: locationGroupError } =
    useGetLocationGroup(locationGroupId);
  const {
    data: schedulePreviewContext,
    isLoading: isSchedulePreviewContextLoading,
    error: schedulePreviewContextError,
  } = useGetSchedulePreviewContext(locationGroupId, locationCode, changeSetId);
  const { data: daypartOptions, isLoading: isLoadingDayparts } =
    useGetAllDayparts();

  const selectedDaypartData = daypartOptions?.find(
    (daypart) => daypart.id === Number(daypartId),
  );

  const schedulePreviewData = useMemo(() => {
    if (!schedulePreviewContext) return undefined;

    if (previewOption === null)
      return schedulePreviewContext as ISchedulePreviewContext;

    const withChangeSet =
      schedulePreviewContext as ISchedulePreviewWithChangeSetContext;

    return previewOption === IPreviewOption.AFTER
      ? withChangeSet.after
      : withChangeSet.before;
  }, [schedulePreviewContext, previewOption]);

  const earliestDefaultAssemblyStartDate = useMemo(() => {
    if (!schedulePreviewContext || !selectedDaypartData) {
      return '';
    }

    const locationChannels = getLocationChannelsFromSchedulePreviewContext(
      schedulePreviewContext,
      changeSetId,
    );

    return getEarliestStartDateFromDefaultAssemblies(
      locationChannels,
      selectedDaypartData.name,
    );
  }, [changeSetId, schedulePreviewContext, selectedDaypartData]);

  const navigate = useApiErrorNavigator(
    locationGroupError ?? schedulePreviewContextError,
  );

  const handleChangeSelectedDate = (newDate: Dayjs) => {
    onChangeParams('date', newDate.format(ISO_DATE_FORMAT));
  };

  useEffect(() => {
    if (!locationGroupId) {
      navigate(PATHS.NOT_FOUND);
    }
  }, [locationGroupId, navigate]);

  // If user manually edits the `date` query and it's invalid or out-of-range,
  // reset it back to the validated date (usually the min date) in the URL.
  useEffect(() => {
    if (!date || !earliestDefaultAssemblyStartDate) return;

    const normalized = getValidDate(
      date,
      earliestDefaultAssemblyStartDate,
    ).format(ISO_DATE_FORMAT);

    if (normalized !== date) {
      onChangeParams('date', normalized, true);
    }
  }, [date, earliestDefaultAssemblyStartDate, onChangeParams]);

  const handleChangeSelectedScreen = (screen: string) =>
    onChangeParams('screen', screen);
  const handleChangeSelectedPosition = (position: string) =>
    onChangeParams('position', position);
  const handleChangeSelectedDaypart = (daypart: string) =>
    onChangeParams('daypartID', daypart);

  return {
    locationGroup,
    selectedDate: getValidDate(date, earliestDefaultAssemblyStartDate),
    minDate: getMinPreviewDate(earliestDefaultAssemblyStartDate),
    maxDate: getMaxPreviewDate(),
    selectedScreen: screen,
    selectedPosition: position,
    selectedDaypart: daypartId,
    previewOption,
    schedulePreviewData,
    schedulePreviewContext,
    isSchedulePreviewContextLoading,
    daypartOptions,
    isLoadingDayparts,
    selectedDaypartData,
    handleChangeSelectedDate,
    handleChangeSelectedScreen,
    handleChangeSelectedPosition,
    handleChangeSelectedDaypart,
    setPreviewOption: setPreviewOptionState,
  };
};
